import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "./Button";
import { Heading } from "./Heading";
import { Text } from "./Text";
import { Price } from "./Price";
import { QuantitySelector } from "./QuantitySelector";
import { Image } from "./Image";
import { getResizedImageUrl, ImagePresets } from "../../utils/imageUtils";
import { useStoreLogoPlaceholder } from "../../hooks/useStoreLogoPlaceholder";
import { useCurrency } from "../../context/CurrencyContext";
import { calculateAvailableInventory, isOutOfStock } from "../../utils/inventoryUtils";
import { SupportedLanguage } from "../../types/translations.types";
import { findProductImage } from "../../utils/productImageUtils";
import { useMemo } from "react";

export interface VariantSelectionModalProps {
 isOpen: boolean;
 onClose: () => void;
 product: {
  _id: string;
  title: string;
  basePrice?: number;
  compareAtPrice?: number;
  isDigital?: boolean;
  variantOptions?: Array<{
   name: string;
   values: Array<{
    value: string;
    addOnPrice: number;
    inventoryQuantity?: number;
    isActive: boolean;
    image?: string;
    locationPricing?: Array<{
     locationId: string;
     price?: number;
     inventoryQuantity?: number;
     isAvailable?: boolean;
    }>;
   }>;
   required: boolean;
  }>;
  variantCombinations?: Array<{
   combination: string;
   price: number;
   inventoryQuantity?: number;
   imageIndex?: number;
   locationPricing?: Array<{
    locationId: string;
    price?: number;
    inventoryQuantity?: number;
    isAvailable?: boolean;
   }>;
  }>;
  locationPricing?: Array<{
   locationId: string;
   price?: number;
   inventoryQuantity?: number;
   isAvailable?: boolean;
  }>;
  images?: Array<{ url: string; isPrimary?: boolean }>;
 };
 onAddToCart: (
  productId: string,
  quantity: number,
  selectedVariantOptions?: Array<{ optionName: string; optionValue: string }>
 ) => Promise<void>;
}

export const VariantSelectionModal: React.FC<VariantSelectionModalProps> = ({
 isOpen,
 onClose,
 product,
 onAddToCart,
}) => {
 const { t, i18n } = useTranslation();
 const { currency, location } = useCurrency();
 const storeLogoPlaceholder = useStoreLogoPlaceholder();
 const [selectedVariantOptions, setSelectedVariantOptions] = useState<Record<string, string>>({});
 const [quantity, setQuantity] = useState(1);
 const [adding, setAdding] = useState(false);

 // Helper function to get translated variant option name
 const getTranslatedOptionName = (option: any): string => {
  const currentLang = (i18n.language || "en").split("-")[0] as SupportedLanguage;
  if (currentLang === "en" || !option.translations) {
   return option.name;
  }
  
  // Handle both Map and plain object formats
  let translation: string | undefined;
  if (option.translations instanceof Map) {
   translation = option.translations.get(currentLang);
  } else if (typeof option.translations === "object") {
   translation = option.translations[currentLang];
  }
  
  return translation || option.name;
 };

 // Helper function to get translated variant option value
 const getTranslatedOptionValue = (value: any): string => {
  const currentLang = (i18n.language || "en").split("-")[0] as SupportedLanguage;
  if (currentLang === "en" || !value.translations) {
   return value.value;
  }
  
  // Handle both Map and plain object formats
  let translation: string | undefined;
  if (value.translations instanceof Map) {
   translation = value.translations.get(currentLang);
  } else if (typeof value.translations === "object") {
   translation = value.translations[currentLang];
  }
  
  return translation || value.value;
 };

 // Reset selections when modal opens/closes
 useEffect(() => {
  if (isOpen) {
   setSelectedVariantOptions({});
   setQuantity(1);
  }
 }, [isOpen]);

 // Ensure quantity is always 1 for digital products
 useEffect(() => {
  if (product.isDigital && quantity !== 1) {
   setQuantity(1);
  }
 }, [product.isDigital, quantity]);

 // Calculate price
 const calculatePrice = (): number => {
  const locationId = location?.locationId;
  
  // Build variant combination string
  const combination = Object.entries(selectedVariantOptions)
   .map(([optionName, optionValue]) => `${optionName}:${optionValue}`)
   .sort()
   .join("|");

  // Check if there's a variant combination with a specific price
  if (combination && product.variantCombinations) {
   const variantCombo = product.variantCombinations.find(
    (vc: any) => vc.combination === combination
   );
   
   if (variantCombo) {
    // Priority: location-specific price > variant combination price
    if (locationId && variantCombo.locationPricing && variantCombo.locationPricing.length > 0) {
     const locationPricing = variantCombo.locationPricing.find(
      (lp: any) => String(lp.locationId) === String(locationId)
     );
     if (locationPricing && locationPricing.price !== undefined) {
      return locationPricing.price;
     }
    }
    
    // Fallback to variant combination base price
    if (variantCombo.price !== undefined) {
     return variantCombo.price;
    }
   }
  }

  // Fallback: Use base product price with location-specific pricing if available
  let price = product.basePrice || 0;
  
  // Check for location-specific base price
  if (locationId && product.locationPricing && product.locationPricing.length > 0) {
   const locationPricing = product.locationPricing.find(
    (lp: any) => String(lp.locationId) === String(locationId)
   );
   if (locationPricing && locationPricing.price !== undefined) {
    return locationPricing.price;
   }
  }

  return price;
 };

 const calculatedPrice = calculatePrice();

 // Check if all variant options are selected (if product has variants)
 const allVariantsSelected = (): boolean => {
  if (!product.variantOptions || product.variantOptions.length === 0) {
   return true; // No variants, so selection is valid
  }
  
  // If product has variants, all options must be selected
  return product.variantOptions.every(
   (option) => selectedVariantOptions[option.name]
  );
 };

 // Check if all required options are selected (for backward compatibility)
 const allRequiredSelected = (): boolean => {
  if (!product.variantOptions || product.variantOptions.length === 0) {
   return true;
  }
  return product.variantOptions.every(
   (option) => !option.required || selectedVariantOptions[option.name]
  );
 };

 // Calculate inventory (with location-specific inventory if available)
 const locationId = location?.locationId;
 const availableInventory = calculateAvailableInventory(product, selectedVariantOptions, locationId);
 const outOfStock = isOutOfStock(product, selectedVariantOptions, locationId);

 const handleVariantOptionChange = (optionName: string, value: string) => {
  setSelectedVariantOptions((prev) => ({
   ...prev,
   [optionName]: value,
  }));
 };

 const handleAddToCart = async () => {
  // If product has variants, all must be selected
  // Don't check outOfStock for digital products
  if (product.variantOptions && product.variantOptions.length > 0) {
   if (!allVariantsSelected() || (outOfStock && !product.isDigital)) return;
  } else if (!allRequiredSelected() || (outOfStock && !product.isDigital)) {
   return;
  }

  try {
   setAdding(true);
   const selectedOptions = Object.entries(selectedVariantOptions).map(([optionName, optionValue]) => ({
    optionName,
    optionValue,
   }));

   await onAddToCart(
    product._id,
    product.isDigital ? 1 : quantity, // Always use quantity 1 for digital products
    selectedOptions.length > 0 ? selectedOptions : undefined
   );
   onClose();
  } catch (error: any) {
   const errorMessage = error?.response?.data?.error || error?.message || "Failed to add item to cart";
   alert(errorMessage);
   console.error("Error adding to cart:", error);
  } finally {
   setAdding(false);
  }
 };

 // Find the appropriate image based on selected variant options
 // Must be called before any early returns (React hooks rule)
 const selectedImage = useMemo(() => {
  return findProductImage(product, selectedVariantOptions);
 }, [product, selectedVariantOptions]);

 const imageUrl = selectedImage
  ? getResizedImageUrl(selectedImage.url, ImagePresets.productCard, storeLogoPlaceholder)
  : storeLogoPlaceholder;

 if (!isOpen) return null;

 return (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
   <div className="bg-white max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
    <div className="p-6">
     {/* Header */}
     <div className="flex items-center justify-between mb-4">
      <Heading size="lg">{product.title}</Heading>
      <button
       onClick={onClose}
       className="text-gray-500 hover:text-gray-700 text-2xl"
      >
       ×
      </button>
     </div>

     <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Product Image */}
      <div className="relative">
       <div className="aspect-square bg-gray-100 overflow-hidden">
        <Image
         src={imageUrl}
         alt={product.title}
         aspectRatio="square"
         objectFit="contain"
         className="w-full h-full"
        />
       </div>
      </div>

      {/* Variant Selection */}
      <div className="space-y-4">
       {/* Price */}
       <div>
        <Price
         amount={calculatedPrice}
         originalAmount={product.compareAtPrice}
         currency={currency}
         size="lg"
         className="text-store-accent font-bold text-xl"
        />
       </div>

       {/* Variant Options */}
       {product.variantOptions && product.variantOptions.length > 0 && (
        <div className="space-y-4">
         {product.variantOptions.map((option) => (
          <div key={option.name}>
           <Text weight="semibold" className="mb-2 text-store-primary">
            {getTranslatedOptionName(option)}
            {option.required && <span className="text-red-500 ml-1">*</span>}:
           </Text>
           <div className="flex flex-wrap gap-2">
            {option.values
             .filter((v) => v.isActive)
             .map((value) => (
              <button
               key={value.value}
               onClick={() => handleVariantOptionChange(option.name, value.value)}
               className={`px-4 py-2 border transition-all text-sm ${
                selectedVariantOptions[option.name] === value.value
                 ? "bg-store-primary text-white border-store-primary"
                 : "bg-white text-store-primary border-store-accent-light hover:border-store-accent"
               }`}
              >
               {getTranslatedOptionValue(value)}
               {value.addOnPrice !== 0 && (
                <span className="ml-1 text-xs">
                 ({value.addOnPrice > 0 ? "+" : ""}
                 {currency} {value.addOnPrice.toFixed(2)})
                </span>
               )}
              </button>
             ))}
           </div>
          </div>
         ))}
        </div>
       )}

       {/* Quantity - Hidden for digital products */}
       {!product.isDigital && (
        <div>
         <div className="flex items-center justify-between mb-2">
          <Text weight="semibold" className="text-store-primary">
           {t("productDetail.quantity")}:
          </Text>
          {availableInventory > 0 && (
           <Text size="sm" color="muted" className="text-gray-600">
            {t("productDetail.available")}: {availableInventory}
          </Text>
          )}
          {outOfStock && !product.isDigital && (
           <Text size="sm" color="error" className="text-red-600 font-semibold">
            {t("productDetail.outOfStock")}
          </Text>
          )}
         </div>
         <QuantitySelector
          value={quantity}
          onChange={setQuantity}
          min={1}
          max={availableInventory > 0 ? availableInventory : 999}
          disabled={outOfStock && !product.isDigital}
         />
        </div>
       )}

       {/* Add to Cart Button */}
       <Button
        onClick={handleAddToCart}
        disabled={(product.variantOptions && product.variantOptions.length > 0 ? !allVariantsSelected() : !allRequiredSelected()) || (outOfStock && !product.isDigital) || adding}
        className={`w-full py-3 ${
         (product.variantOptions && product.variantOptions.length > 0 ? !allVariantsSelected() : !allRequiredSelected()) || (outOfStock && !product.isDigital) || adding
          ? "bg-gray-400 cursor-not-allowed text-white"
          : "bg-store-primary hover:bg-store-primary-dark text-white"
        }`}
       >
        {adding
         ? t("productDetail.adding")
         : (product.variantOptions && product.variantOptions.length > 0 ? !allVariantsSelected() : !allRequiredSelected())
         ? t("productDetail.selectVariants")
         : (outOfStock && !product.isDigital)
         ? t("productDetail.outOfStock")
         : t("productDetail.addToCart")}
       </Button>
      </div>
     </div>
    </div>
   </div>
  </div>
 );
};

